import { useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReCAPTCHA from 'react-google-recaptcha';

import axios from 'axios';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';

import { BarsLoadingIndicator } from 'components/common/barsLoadingIndicator';
import { Input } from '../common/input';
import { Modal } from 'components/common/modal';

import { closeModal } from 'features/modalSlice';

import { apiConfig, COOKIE_NAME } from '../../config';
import { useGetBookingData } from 'hooks';
import { getDifferenceInDays, getCookie } from 'lib';
import { noReCaptchaPublicKey } from '../../constants';

import './ContactUsModal.css';

const ContactUsModal = () => {
    const dispatch = useDispatch();
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [reCaptchaValue, setReCaptchaValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const reCaptchaRef = useRef(null);

    const colors = useSelector(
        (state) => state.miniBookingWidget?.data?.colors
    );
    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);
    const propertyID = useSelector(
        (state) => state.miniBookingWidget?.data?.propertyID
    );
    const ourPrice = useSelector(
        (state) => state.miniBookingWidget?.data?.ourPrice
    );

    const reCapatchPublicKey =
        process.env.NODE_ENV === 'production'
            ? useSelector(
                  (state) => state.miniBookingWidget?.data?.reCaptchaPublicKey
              )
            : process.env.REACT_APP_RE_CAPTCHA_PUBLIC_KEY;

    // On production ignore reCaptcha if the public key is not provided
    const ignoreReCaptch = !reCapatchPublicKey;

    const isOpen = useSelector((state) => state.modal.isContactUsModalOpen);

    const handleModalClose = useCallback(() => {
        dispatch(closeModal({ modalId: 'ContactUsModal' }));
        // Reset
        setErrors({});
        setFullName('');
        setEmail('');
        setPhone('');
        setMessage('');
        setReCaptchaValue('');
    });

    const { userBookingData } = useGetBookingData();

    const handleSubmit = async (event) => {
        event.preventDefault();
        const errors = validateInputs();
        const token = reCapatchPublicKey
            ? reCaptchaRef.current.getValue()
            : noReCaptchaPublicKey;

        if (!isEmpty(errors)) {
            setErrors(errors);
            return;
        }

        const cookie = getCookie(COOKIE_NAME);
        const selectedDays = getDifferenceInDays(
            userBookingData?.checkout,
            userBookingData?.checkin
        );

        try {
            setIsLoading(true);
            const response = await axios.post(apiConfig?.contactUsUrl, {
                ...userBookingData,
                curCode: userBookingData?.currency,
                lang: userBookingData?.localeLanguage,
                fullName,
                email,
                phone,
                message,
                token,
                cookie,
                propertyID,
                price: ourPrice?.price,
                selecteddays: selectedDays,
                timeofday: Date.now(),
            });

            if (response?.data.toLowerCase() === 'ok') {
                toast.success(Lang?.successMessage, {
                    autoClose: true,
                    theme: 'colored',
                });
            }
        } catch (error) {
            toast.error(Lang?.networkError, {
                autoClose: true,
                theme: 'colored',
            });
        }

        handleModalClose();
        setIsLoading(false);

        if (reCaptchaRef.current) {
            reCaptchaRef.current.reset();
        }
    };

    // TODO: IMPROVE THIS (KEEP IT DRY)
    const validateInputs = () => {
        const errors = {};

        const emailRegex =
            // eslint-disable-next-line no-useless-escape
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (!fullName) {
            errors.fullName = Lang?.requiredField;
        }

        if (!email) {
            errors.email = Lang?.requiredField;
        } else if (!email.match(emailRegex)) {
            errors.email = Lang?.pleaseEnterValidEmail;
        }

        if (!phone) {
            errors.phone = Lang?.requiredField;
        }

        if (!message) {
            errors.message = Lang?.requiredField;
        }

        if (!ignoreReCaptch && !reCaptchaValue) {
            errors.reCaptcha = Lang?.pleaseCompleteRecaptcha;
        }

        return errors;
    };

    const renderContent = () => {
        return (
            <div className="ab-contact-us-cnt ab-container">
                <h5 className="ab-contact-us-heading-text">
                    {Lang?.howCanWeAssist}
                </h5>
                <p className="ab-contact-us-prompt-text">
                    {Lang?.qualityHasAlwaysBeen}
                </p>

                <h6 className="ab-contact-us-form-fill-up-text">
                    {Lang?.fillUpThisForm}
                </h6>

                <form
                    className="ab-contact-us-form ab-form"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="text"
                                placeholder={Lang?.fullName}
                                value={fullName}
                                onChange={(event) => {
                                    setFullName(event.target.value);
                                }}
                                isInvalid={Boolean(errors?.fullName)}
                                errorMsg={errors.fullName}
                                isRequired
                                id="contact-us-full-name"
                                autoFocus
                                label={Lang?.fullName}
                            />
                        </div>
                    </div>

                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="email"
                                placeholder={Lang?.email}
                                value={email}
                                onChange={(event) => {
                                    setEmail(event.target.value);
                                }}
                                isInvalid={Boolean(errors?.email)}
                                errorMsg={errors.email}
                                isRequired
                                id="contact-us-email"
                                label={Lang?.email}
                            />
                        </div>
                    </div>

                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="text"
                                placeholder={Lang?.phone}
                                value={phone}
                                onChange={(event) => {
                                    setPhone(event.target.value);
                                }}
                                isInvalid={Boolean(errors?.phone)}
                                errorMsg={errors.phone}
                                isRequired
                                id="contact-us-phone"
                                label={Lang?.phone}
                            />
                        </div>
                    </div>

                    <div className="ab-input-group">
                        <div className="ab-input-field-cnt">
                            <Input
                                type="textarea"
                                isRequired
                                value={message}
                                onChange={(event) => {
                                    setMessage(event.target.value);
                                }}
                                isInvalid={Boolean(errors?.message)}
                                errorMsg={errors.message}
                                id="contact-us-message"
                                label={Lang?.message}
                            />
                        </div>
                    </div>
                    {!!reCapatchPublicKey && (
                        <div className="ab-recaptcha-cnt">
                            <ReCAPTCHA
                                sitekey={reCapatchPublicKey}
                                onChange={(value) => setReCaptchaValue(value)}
                                ref={reCaptchaRef}
                            />
                            {errors.reCaptcha && (
                                <div className="ab-input-error-msg ab-input-error-msg--recaptcha">
                                    {errors.reCaptcha}
                                </div>
                            )}
                        </div>
                    )}

                    <p className="ab-info-declartion-statement">
                        {Lang?.iHerebyCertifyThatInfo}
                    </p>

                    <button
                        type="submit"
                        style={{
                            backgroundColor: colors?.primaryBackgroundColor,
                            color: colors?.primaryTextColor,
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? <BarsLoadingIndicator /> : Lang?.submit}
                    </button>
                </form>
            </div>
        );
    };

    return (
        <Modal
            className="ab-modal--contact-us"
            color={colors?.primaryTextColor}
            backgroundColor={colors?.primaryBackgroundColor}
            isOpen={isOpen}
            onClose={handleModalClose}
            onOverlayClick={handleModalClose}
            placement="center"
            renderContent={renderContent}
            styles={{
                closeIcon: { color: colors?.primaryTextColor },
            }}
            title={Lang?.contactUs}
            variant="secondary"
            width={400}
        />
    );
};

export default ContactUsModal;
